'use client';

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { useConnectorStore } from '@/lib/connectors/store';
import type { ConnectorOperation, OperationParameter } from '@/lib/connectors/types';
import { Plus, Trash2 } from 'lucide-react';

interface ParameterBuilderProps {
  connectorId: string;
  operation: ConnectorOperation;
}

const PARAM_TYPES = [
  { value: 'string', label: 'String' },
  { value: 'number', label: 'Number' },
  { value: 'boolean', label: 'Boolean' },
  { value: 'array', label: 'Array' },
  { value: 'object', label: 'Object' },
];

export function ParameterBuilder({ connectorId, operation }: ParameterBuilderProps) {
  const { updateOperation } = useConnectorStore();

  const pathParams = operation.path_parameters || [];
  const queryParams = operation.query_parameters || [];
  const headerParams = operation.headers || [];

  const updateParams = async (
    type: 'path_parameters' | 'query_parameters' | 'headers',
    params: OperationParameter[]
  ) => {
    try {
      await updateOperation(connectorId, operation.id, { [type]: params });
    } catch (error) {
      console.error('Failed to update parameters:', error);
    }
  };

  const addParameter = (type: 'path_parameters' | 'query_parameters' | 'headers') => {
    const params = type === 'path_parameters' ? pathParams : type === 'query_parameters' ? queryParams : headerParams;
    updateParams(type, [
      ...params,
      { name: '', type: 'string', required: false },
    ]);
  };

  const updateParameter = (
    type: 'path_parameters' | 'query_parameters' | 'headers',
    index: number,
    updates: Partial<OperationParameter>
  ) => {
    const params = type === 'path_parameters' ? [...pathParams] : type === 'query_parameters' ? [...queryParams] : [...headerParams];
    params[index] = { ...params[index], ...updates };
    updateParams(type, params);
  };

  const deleteParameter = (type: 'path_parameters' | 'query_parameters' | 'headers', index: number) => {
    const params = type === 'path_parameters' ? pathParams : type === 'query_parameters' ? queryParams : headerParams;
    updateParams(type, params.filter((_, i) => i !== index));
  };

  const renderParameterSection = (
    title: string,
    type: 'path_parameters' | 'query_parameters' | 'headers',
    params: OperationParameter[]
  ) => (
    <AccordionItem value={type}>
      <AccordionTrigger>
        <div className="flex items-center gap-2">
          <span>{title}</span>
          <Badge variant="secondary">{params.length}</Badge>
        </div>
      </AccordionTrigger>
      <AccordionContent>
        <div className="space-y-4 pt-2">
          {params.map((param, index) => (
            <div key={index} className="group p-4 border rounded-lg space-y-3">
              <div className="flex items-start gap-4">
                <div className="flex-1 grid grid-cols-3 gap-4">
                  <div className="space-y-1">
                    <Label className="text-xs">Name</Label>
                    <Input
                      value={param.name}
                      onChange={(e) => updateParameter(type, index, { name: e.target.value })}
                      placeholder="parameter_name"
                      className="font-mono"
                    />
                  </div>

                  <div className="space-y-1">
                    <Label className="text-xs">Type</Label>
                    <Select
                      value={param.type}
                      onValueChange={(value: OperationParameter['type']) =>
                        updateParameter(type, index, { type: value })
                      }
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {PARAM_TYPES.map((t) => (
                          <SelectItem key={t.value} value={t.value}>
                            {t.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-1">
                    <Label className="text-xs">Description</Label>
                    <Input
                      value={param.description || ''}
                      onChange={(e) => updateParameter(type, index, { description: e.target.value })}
                      placeholder="Description"
                    />
                  </div>
                </div>

                <Button
                  variant="ghost"
                  size="icon"
                  className="text-destructive opacity-0 group-hover:opacity-100"
                  onClick={() => deleteParameter(type, index)}
                >
                  <Trash2 className="size-4" />
                </Button>
              </div>

              <div className="flex items-center gap-4 ml-0">
                <div className="flex items-center gap-2">
                  <Switch
                    checked={param.required}
                    onCheckedChange={(checked) => updateParameter(type, index, { required: checked })}
                  />
                  <Label className="text-sm">Required</Label>
                </div>
              </div>
            </div>
          ))}

          <Button variant="outline" size="sm" onClick={() => addParameter(type)}>
            <Plus className="size-4 mr-2" />
            Add Parameter
          </Button>
        </div>
      </AccordionContent>
    </AccordionItem>
  );

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle>Parameters</CardTitle>
            <CardDescription>
              Define path, query, and header parameters
            </CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <Accordion type="multiple" defaultValue={['path_parameters', 'query_parameters', 'headers']}>
          {renderParameterSection('Path Parameters', 'path_parameters', pathParams)}
          {renderParameterSection('Query Parameters', 'query_parameters', queryParams)}
          {renderParameterSection('Headers', 'headers', headerParams)}
        </Accordion>
      </CardContent>
    </Card>
  );
}
